import React, { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Project } from '../types';

interface CreateProjectProps {
  onCreateProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onBack: () => void;
}

export const CreateProject: React.FC<CreateProjectProps> = ({
  onCreateProject,
  onBack,
}) => {
  const [name, setName] = useState('');
  const [specContent, setSpecContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sampleInputData, setSampleInputData] = useState<any | null>(null);
  const [sampleOutputData, setSampleOutputData] = useState<any | null>(null);
  const [inputFileName, setInputFileName] = useState<string>('');
  const [outputFileName, setOutputFileName] = useState<string>('');
  const isFormValid = Boolean(name.trim() && specContent.trim() && sampleInputData && sampleOutputData);
  const [showInputModal, setShowInputModal] = useState(false);
  const [showOutputModal, setShowOutputModal] = useState(false);

  const parseJsonFile = (file: File): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = (reader.result as string) || '';
          const json = JSON.parse(text);
          resolve(json);
        } catch (e) {
          reject(new Error('Invalid JSON file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const onSelectSampleInput = async (file: File) => {
    try {
      const json = await parseJsonFile(file);
      setSampleInputData(json);
      setInputFileName(file.name);
      setShowInputModal(false);
    } catch (e) {
      alert('Invalid Sample Input JSON. Please upload a valid .json file.');
    }
  };

  const onSelectSampleOutput = async (file: File) => {
    try {
      const json = await parseJsonFile(file);
      setSampleOutputData(json);
      setOutputFileName(file.name);
      setShowOutputModal(false);
    } catch (e) {
      alert('Invalid Sample Output JSON. Please upload a valid .json file.');
    }
  };

  const handleDrop = async (which: 'input' | 'output', e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const looksJson = file.type.includes('json') || file.name.toLowerCase().endsWith('.json');
    if (!looksJson) {
      alert('Please drop a .json file');
      return;
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (which === 'input') {
        setSampleInputData(parsed);
        setInputFileName(file.name);
        setShowInputModal(false);
      } else {
        setSampleOutputData(parsed);
        setOutputFileName(file.name);
        setShowOutputModal(false);
      }
    } catch {
      alert('Invalid JSON in dropped file');
    }
  };

  const handlePaste = (which: 'input' | 'output', e: React.ClipboardEvent<HTMLDivElement>) => {
    if (isSubmitting) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    try {
      const parsed = JSON.parse(text);
      if (which === 'input') {
        setSampleInputData(parsed);
        setInputFileName('Pasted JSON');
        setShowInputModal(false);
      } else {
        setSampleOutputData(parsed);
        setOutputFileName('Pasted JSON');
        setShowOutputModal(false);
      }
    } catch {
      alert('Pasted content is not valid JSON');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !specContent.trim() || !sampleInputData || !sampleOutputData) {
      alert('Please fill in all required fields, including sample input/output JSON');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateProject({
        name: name.trim(),
        specContent: specContent.trim(),
        // @ts-ignore: passing extras consumed by hook
        sampleInputData,
        sampleOutputData,
      });
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={onBack}
            className="inline-flex items-center text-zinc-600 hover:text-zinc-900 mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Projects
          </button>

          <div className="rounded-2xl">
            <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight mb-2">
              Create New Project
            </h1>
            <p className="text-zinc-600 mb-8">
              Set up a new data annotation project with your specifications
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white placeholder:text-zinc-400 border border-zinc-200 shadow-none focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-colors disabled:bg-zinc-100 disabled:cursor-not-allowed"
                  placeholder="Enter project name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Sample Input/Output upload controls */}
              <div className="pt-2">
                <div className="space-y-2">
                  {/* Labels row */}
                  <div className="flex justify-between">
                    <span className="block text-sm text-zinc-700">Sample input data (JSON) *</span>
                    <span className="block text-sm text-zinc-700">Sample output data (JSON) *</span>
                  </div>

                  {/* Buttons + connecting arrow */}
                  <div className="flex items-center justify-between">
                    {/* Left button */}
                    <div className="flex-1 max-w-sm flex flex-col items-start">
                      <button
                        type="button"
                        onClick={() => setShowInputModal(true)}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-full text-zinc-900 bg-white ring-1 ring-zinc-300 hover:bg-zinc-50 hover:ring-zinc-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                      >
                        Choose JSON
                      </button>
                      {inputFileName && (
                        <p className="mt-1 text-xs text-zinc-500">Selected: {inputFileName}</p>
                      )}
                    </div>

                    {/* Middle arrow strictly between buttons */}
                     <div className="flex-1 mx-6 h-10 flex items-center">
                       <svg className="w-full h-6" viewBox="0 0 100 12" preserveAspectRatio="none">
                        <defs>
                          <marker id="arrowhead" markerUnits="userSpaceOnUse" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                            <path d="M0,0 L0,8 L8,4 z" fill="black" />
                          </marker>
                        </defs>
                         <line x1="0" y1="6" x2="100" y2="6" stroke="black" strokeWidth="1" markerEnd="url(#arrowhead)" />
                      </svg>
                    </div>

                    {/* Right button */}
                    <div className="flex-1 max-w-sm flex flex-col items-end">
                      <button
                        type="button"
                        onClick={() => setShowOutputModal(true)}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-full text-zinc-900 bg-white ring-1 ring-zinc-300 hover:bg-zinc-50 hover:ring-zinc-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                      >
                        Choose JSON
                      </button>
                      {outputFileName && (
                        <p className="mt-1 text-xs text-zinc-500">Selected: {outputFileName}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-4">
                  Project Specification *
                </label>
                
                <textarea
                  value={specContent}
                  onChange={(e) => setSpecContent(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white placeholder:text-zinc-400 border border-zinc-200 shadow-none focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-colors disabled:bg-zinc-100 disabled:cursor-not-allowed"
                  placeholder="Enter your project specifications, annotation guidelines, or requirements..."
                  rows={8}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex justify-between items-center pt-8">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={isSubmitting}
                  className="px-6 py-3 rounded-full bg-white text-zinc-900 ring-1 ring-zinc-300 hover:bg-zinc-50 hover:ring-zinc-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                  className="px-6 py-3 text-white rounded-full transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--accent)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
                >
                  {isSubmitting ? 'Creating...' : 'Create Project'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Input JSON Modal */}
      {showInputModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowInputModal(false)} />
          <div
            className="relative z-10 w-full max-w-xl rounded-2xl bg-white/95 p-6 shadow-xl"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop('input', e)}
            onPaste={(e) => handlePaste('input', e)}
          >
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Sample input data (JSON)</h3>
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">Paste JSON, drag & drop a file, or choose a file.</p>
              <input
                type="file"
                accept="application/json,.json"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onSelectSampleInput(f);
                }}
                disabled={isSubmitting}
                className="block w-full text-sm text-zinc-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-full bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                onClick={() => setShowInputModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
                onClick={() => setShowInputModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Output JSON Modal */}
      {showOutputModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowOutputModal(false)} />
          <div
            className="relative z-10 w-full max-w-xl rounded-2xl bg-white/95 p-6 shadow-xl"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop('output', e)}
            onPaste={(e) => handlePaste('output', e)}
          >
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Sample output data (JSON)</h3>
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">Paste JSON, drag & drop a file, or choose a file.</p>
              <input
                type="file"
                accept="application/json,.json"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onSelectSampleOutput(f);
                }}
                disabled={isSubmitting}
                className="block w-full text-sm text-zinc-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-full bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                onClick={() => setShowOutputModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-full bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
                onClick={() => setShowOutputModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};